"use client";

import { motion } from "framer-motion";
import { ShoppingBag, ExternalLink, Feather, ShieldCheck, Truck, Gem } from "lucide-react";
import { PACKS, SIGNATURE } from "@/data/catalog";
import { formatPrix } from "./types";

const PRIX_PACKS: (number | null)[] = [79.9, 129.9, 189.9, 34.9, null];

/* ─────────────── Packs E-commerce ─────────────── */
export function PacksView({ onAddPack }: { onAddPack: (idx: number) => void }) {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-20">
      <div className="text-center mb-12">
        <p className="tracking-[0.35em] text-[#D4AF37] text-xs uppercase mb-3">La boutique en ligne</p>
        <h1 className="font-display text-4xl md:text-6xl font-bold text-[#F5EFE0]">
          Les <span className="gold-text">Packs E-commerce</span> de l&rsquo;Empire
        </h1>
        <p className="text-[#F5EFE0]/65 max-w-2xl mx-auto mt-4">
          Coffrets d&rsquo;exception, caisses familiales et livraisons scellées à la
          cire d&rsquo;or — l&rsquo;expérience d&rsquo;ouverture digne d&rsquo;un palais,
          prête pour votre boutique.
        </p>
        <div className="gold-line w-80 mx-auto mt-6" />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {PACKS.map((p, i) => {
          const prix = PRIX_PACKS[i];
          return (
            <motion.figure
              key={p.file}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="card-empire rounded-2xl overflow-hidden flex flex-col"
            >
              <img src={p.file} alt={p.nom} className="w-full aspect-[5/4] object-cover" loading="lazy" />
              <figcaption className="p-5 md:p-6 flex flex-col gap-3 flex-1">
                <h3 className="font-display text-xl md:text-2xl font-bold text-[#F5EFE0]">{p.nom}</h3>
                <p className="text-sm text-[#F5EFE0]/60 leading-relaxed flex-1">{p.desc}</p>
                <div className="flex items-center justify-between gap-3 pt-1">
                  {prix !== null ? (
                    <>
                      <span className="font-display text-2xl font-bold gold-text">{formatPrix(prix)}</span>
                      <button
                        onClick={() => onAddPack(i)}
                        className="gold-btn px-5 py-2.5 rounded-xl font-semibold text-sm inline-flex items-center gap-2"
                      >
                        <ShoppingBag className="h-4 w-4" /> Ajouter
                      </button>
                    </>
                  ) : (
                    <span className="text-xs tracking-widest uppercase text-[#F6E27A] border border-[#D4AF37]/40 rounded-full px-4 py-2">
                      Visuel officiel de la boutique
                    </span>
                  )}
                </div>
              </figcaption>
            </motion.figure>
          );
        })}
      </div>

      {/* Garanties */}
      <div className="grid sm:grid-cols-3 gap-4 mt-14">
        {[
          { icone: Truck, titre: "Livraison Impériale", texte: "Colis pourpre & or, sceau de cire, sacs de velours brodés." },
          { icone: ShieldCheck, titre: "Authenticité Scellée", texte: "Chaque coffret porte la signature complète d'Adji Anta Diop." },
          { icone: Gem, titre: "405 Créations", texte: "Treize marques de prestige réunies dans une seule boutique." },
        ].map(({ icone: Icone, titre, texte }) => (
          <div key={titre} className="card-empire rounded-2xl p-6 text-center">
            <Icone className="h-7 w-7 mx-auto text-[#D4AF37]" />
            <p className="font-display font-bold text-[#F5EFE0] mt-3">{titre}</p>
            <p className="text-xs text-[#F5EFE0]/55 mt-1.5 leading-relaxed">{texte}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────── La Signature ─────────────── */
const SITES = [
  { nom: "Seydina Muhammad Éditions — Médine", url: "https://seydinamuhammadeditions.com/Medine" },
  { nom: "Ndiamsyl Mody — Premium Water Excellence", url: "https://ndiamsyl-mody.online/mody/2/" },
  { nom: "Al-Maoun", url: "https://Al-Maoun.online" },
  { nom: "Pape Ndiaw", url: "https://papendiaw.ndiaye.info" },
];

export function SignatureView() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 md:py-24">
      <div className="text-center">
        <Feather className="h-8 w-8 mx-auto text-[#D4AF37]" />
        <h1 className="font-display text-4xl md:text-6xl font-bold text-[#F5EFE0] mt-4">
          La <span className="gold-text">Signature</span> Impériale
        </h1>
        <div className="gold-line w-72 mx-auto mt-6" />
      </div>

      <motion.blockquote
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8 }}
        className="mt-14 text-center"
      >
        <p className="font-display italic text-3xl md:text-5xl gold-text leading-snug">
          « {SIGNATURE} »
        </p>
        <p className="text-sm tracking-[0.3em] uppercase text-[#F5EFE0]/50 mt-6">
          Apposée sur les 405 créations de l&rsquo;Empire
        </p>
      </motion.blockquote>

      <div className="mt-16 space-y-6 text-[#F5EFE0]/70 leading-relaxed">
        <p>
          Sous l&rsquo;égide de <strong className="text-[#F5EFE0]">Son Excellence Président
          Ndongo Ndiaye</strong>, l&rsquo;Empire rassemble treize marques nées d&rsquo;une
          même promesse : offrir le meilleur. Des canettes de bouye aux velours
          cardinal aux flacons d&rsquo;eau cristalline du Prince des Voluptés, chaque
          étiquette porte une couronne royale, un ruban de soie ivoire et la
          signature fondatrice de la Maison.
        </p>
        <p>
          Des eaux impériales de la Maison Ndiamsyl Mody aux parfums d&rsquo;encens
          de Touba de l&rsquo;Excellence Mouride-Baye Fall, en passant par les
          nectars de baobab, la volaille de Guinaru Antayi, les merveilles
          bovines et laitières de Sunu Naggi Mame et les parfums onyx de Maoun —
          un empire entier stylisé en 405 créations photoréalistes, prêtes pour
          la boutique en ligne, l&rsquo;affichage et les coffrets de gala.
        </p>
      </div>

      <div className="mt-14">
        <h2 className="font-display text-xl font-bold text-[#F5EFE0] mb-4">Les Maisons sœurs en ligne</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {SITES.map((s) => (
            <a
              key={s.url}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className="card-empire rounded-xl px-5 py-4 flex items-center justify-between gap-3 text-sm text-[#F5EFE0]/80 hover:text-[#F6E27A] transition-colors"
            >
              <span>{s.nom}</span>
              <ExternalLink className="h-4 w-4 shrink-0 text-[#D4AF37]" />
            </a>
          ))}
        </div>
      </div>

      <p className="text-center text-xs text-[#F5EFE0]/40 italic mt-16">
        Propriété de Son Excellence Président Ndongo Ndiaye — Boutique de
        démonstration, aucune transaction réelle.
      </p>
    </div>
  );
}
